import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { InkedTitle } from '../components/InkedTitle';
import { Reveal } from '../components/Reveal';
import { useBooking } from '../context/BookingContext';
import { artists, getWorksByArtist, styleLabels, type Artist } from '../data';
import { useLang } from '../hooks/useLang';

export function ArtistsPage() {
  const { t } = useTranslation();
  const lang = useLang();

  return (
    <>
      <section className="px-6 pt-36 pb-16 md:px-10 md:pt-48 md:pb-24">
        <div className="mx-auto max-w-[1800px]">
          <p className="text-base font-display lowercase text-ink-muted tracking-tight">
            {t('artist.indexEyebrow').toLowerCase()} · 04
          </p>
          <InkedTitle
            as="h1"
            immediate
            delay={0.15}
            className="mt-6 font-display tracking-display leading-[0.85] text-[clamp(3.5rem,12vw,13rem)] lowercase"
          >
            {t('artist.indexTitle')}
          </InkedTitle>
          <p className="mt-10 max-w-2xl text-lg md:text-xl text-ink-paper/75">
            {t('artist.indexLead')}
          </p>
        </div>
      </section>

      {artists.map((artist, i) => (
        <ArtistScene key={artist.slug} artist={artist} index={i} reversed={i % 2 === 1} lang={lang} />
      ))}
    </>
  );
}

type SceneProps = {
  artist: Artist;
  index: number;
  reversed: boolean;
  lang: 'it' | 'en';
};

function ArtistScene({ artist, index, reversed, lang }: SceneProps) {
  const { t } = useTranslation();
  const { openBooking } = useBooking();
  const worksCount = getWorksByArtist(artist.slug).length;

  return (
    <section className="relative border-t border-ink-line/40 overflow-hidden">
      <span
        aria-hidden
        className="pointer-events-none absolute -top-12 right-6 md:right-10 font-display tracking-display text-[clamp(8rem,28vw,28rem)] leading-none lowercase text-ink-line/40 select-none"
      >
        0{index + 1}
      </span>

      <div className="relative mx-auto max-w-[1800px] px-6 md:px-10 py-16 md:py-32">
        <div className="grid gap-10 md:grid-cols-12 items-center">
          <Reveal
            className={`md:col-span-7 ${reversed ? 'md:order-2' : 'md:order-1'}`}
            y={32}
          >
            <Link
              to={`/artisti/${artist.slug}`}
              className="group block relative aspect-[4/5] md:aspect-[4/5] overflow-hidden bg-ink-line"
            >
              <motion.img
                src={artist.portrait}
                alt={artist.name}
                loading={index < 1 ? 'eager' : 'lazy'}
                decoding="async"
                initial={false}
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 h-full w-full object-cover ink-photo group-hover:ink-photo-hover"
              />
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between text-ink-paper">
                <span className="text-sm font-display lowercase tracking-tight bg-ink-base/50 backdrop-blur-sm px-2 py-1">
                  @{artist.instagram}
                </span>
                <span className="text-sm font-display lowercase tracking-tight bg-ink-base/50 backdrop-blur-sm px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {t('common.viewMore')} ↗
                </span>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-8 bg-gradient-to-t from-ink-base/85 via-ink-base/30 to-transparent">
                <p className="text-sm font-display lowercase tracking-tight text-ink-paper/85">
                  {artist.origin} → {artist.basedIn} · {t('common.since')} {artist.since}
                </p>
              </div>
            </Link>
          </Reveal>

          <Reveal
            className={`md:col-span-5 ${reversed ? 'md:order-1 md:pr-8' : 'md:order-2 md:pl-8'} grid content-start gap-8`}
            y={24}
            delay={0.1}
          >
            <div className="grid gap-3">
              <p className="text-sm font-display lowercase tracking-tight text-ink-muted">
                {artist.alias}
              </p>
              <Link
                to={`/artisti/${artist.slug}`}
                className="font-display tracking-display leading-[0.85] text-[clamp(3rem,9vw,8rem)] lowercase"
              >
                {artist.name}
              </Link>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-ink-muted">
                {artist.styles.map((s) => styleLabels[s][lang]).join(' · ')}
              </p>
            </div>

            <p className="font-display tracking-display text-2xl md:text-3xl lg:text-4xl leading-[1.1] text-ink-paper">
              «{artist.manifesto[lang]}»
            </p>

            <p className="text-base md:text-lg text-ink-paper/80 leading-relaxed line-clamp-4 max-w-xl">
              {artist.bio[lang]}
            </p>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-ink-line/40 pt-6 max-w-md">
              <div>
                <dt className="text-sm font-display lowercase tracking-tight text-ink-muted">
                  {t('artist.worksLabel')}
                </dt>
                <dd className="mt-2 font-display tracking-display text-3xl leading-none">
                  {worksCount}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-display lowercase tracking-tight text-ink-muted">
                  {t('artist.sinceLabel')}
                </dt>
                <dd className="mt-2 font-display tracking-display text-3xl leading-none">
                  {artist.since}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-3">
              <Link
                to={`/artisti/${artist.slug}`}
                className="group inline-flex items-center justify-between gap-3 px-6 py-4 bg-ink-paper text-ink-base text-xs md:text-sm uppercase tracking-[0.22em] hover:bg-ink-accent hover:text-ink-paper transition-colors min-w-[220px]"
              >
                <span>{t('common.viewMore')}</span>
                <span aria-hidden className="transition-transform group-hover:translate-x-1">
                  →
                </span>
              </Link>
              <button
                type="button"
                onClick={() => openBooking({ artistSlug: artist.slug })}
                className="group inline-flex items-center justify-between gap-3 px-6 py-4 border border-ink-line/60 hover:border-ink-paper text-xs md:text-sm uppercase tracking-[0.22em] transition-colors min-w-[220px]"
              >
                <span>{t('nav.book')}</span>
                <span aria-hidden className="transition-transform group-hover:translate-x-1">
                  →
                </span>
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
